import type {
  Service,
  ServiceUsage,
  ServiceAddon,
} from '../types/service.types';
import { supabase } from '@/shared/lib/supabase';

/**
 * Hosting service service.
 *
 * `list()` and `getById()` call the `whm-account-summary` Edge Function which
 * reads the customer's `customer_link` rows (RLS-scoped), fetches each cPanel
 * account live from WHM, and joins with `service_plan`.
 *
 * `getUsage()` and `listAddons()` return empty until the corresponding Edge
 * Functions / tables land — UI shows "Coming soon".
 *
 * `createCpanelSession()` throws until the `whm-cpanel-sso` Edge Function is wired.
 */

interface AccountSummaryResponse {
  services: Service[];
  staleAt: string;
}

async function fetchLiveServices(): Promise<Service[]> {
  const { data, error } = await supabase.functions.invoke<AccountSummaryResponse>(
    'whm-account-summary'
  );
  if (error) {
    throw new Error(`Could not load services: ${error.message}`);
  }
  if (!data || !Array.isArray(data.services)) {
    throw new Error('whm-account-summary returned unexpected payload');
  }
  return data.services;
}

export const serviceService = {
  async list(): Promise<Service[]> {
    return fetchLiveServices();
  },

  async getById(id: string): Promise<Service | null> {
    const all = await fetchLiveServices();
    return all.find((s) => s.id === id) ?? null;
  },

  async getUsage(_serviceId: string): Promise<ServiceUsage | null> {
    // Pending: WHM `accountusage` Edge Function. Null drives the "Coming soon" UI state.
    return null;
  },

  async listAddons(_serviceId: string): Promise<ServiceAddon[]> {
    // Pending: addons table + Edge Function. Empty array drives the "Coming soon" UI state.
    return [];
  },

  /**
   * Issue a one-time cPanel SSO URL via the whm-cpanel-sso v2 EF.
   *
   * Throws CpanelSsoError on EF rejection — UI should switch on `.code`
   * to render the right empty-state copy. The URL is returned but should
   * be redirected-to immediately, not stashed (single-use, ~5min TTL).
   */
  async createCpanelSession(input: { cpanel_username: string }): Promise<{ url: string; expires_at: string }> {
    const { data, error } = await supabase.functions.invoke<{
      ok: true;
      url: string;
      expires_at: string;
    }>('whm-cpanel-sso', { body: { cpanel_username: input.cpanel_username } });

    if (error) {
      // Supabase wraps EF non-2xx in error.context (a Response). Try to read
      // the structured error body so we can preserve the EF error code.
      let code = 'internal_error';
      let message = error.message || 'Could not open cPanel.';
      let details: Record<string, unknown> | undefined;
      const ctx = (error as { context?: Response }).context;
      if (ctx && typeof ctx.json === 'function') {
        try {
          const parsed = await ctx.clone().json();
          if (parsed && typeof parsed === 'object') {
            const p = parsed as { code?: string; message?: string; details?: Record<string, unknown> };
            code = p.code ?? code;
            message = p.message ?? message;
            details = p.details;
          }
        } catch {
          // body wasn't JSON — keep the generic message
        }
      }
      throw new CpanelSsoError(code, message, details);
    }

    if (!data || !data.url) {
      throw new CpanelSsoError('internal_error', 'cPanel SSO returned no URL.');
    }
    return { url: data.url, expires_at: data.expires_at };
  },
};

/**
 * Typed exception for cPanel SSO failures. UI should switch on `.code`:
 *
 *   email_not_verified       → "Verify your email to continue"
 *   forbidden_not_owner      → "You don't have access to this account"
 *   service_suspended        → use `details.latest_unpaid_invoice_id`
 *   rate_limited             → "Too many attempts — wait a few minutes"
 *   whm_session_failed       → "cPanel server unreachable — try again"
 *   internal_error           → fallback
 */
export class CpanelSsoError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'CpanelSsoError';
  }

  get latestUnpaidInvoiceId(): string | null {
    const v = this.details?.latest_unpaid_invoice_id;
    return typeof v === 'string' ? v : null;
  }
}
